export type IssueStatus = 'enabled' | 'disabled';

/** One issue marker or arrow above the plot, ready to render. */
export type IssueAlertModel = {
  /** React key. Index-based for issue markers. */
  key: string;
  id: string;
  label: string;
  /** Stacking order; also the button's z-index. */
  index: number;
  left?: number;
  right?: number;
  /** Issue markers highlight while their issue is hovered; arrows never do. */
  highlightable: boolean;
};

/** Markers show only while annotations are on, issues exist, and issues are enabled. */
export function getIssuesWrapperStatus({
  annotationActive,
  issueCount,
  issueRampStatus,
}: {
  annotationActive: boolean;
  issueCount: number;
  issueRampStatus: IssueStatus;
}): IssueStatus {
  const failConditions = [
    !annotationActive,
    issueCount === 0,
    issueRampStatus === 'disabled',
  ];

  return failConditions.some(Boolean) ? 'disabled' : 'enabled';
}

/** Icons sit 6px left of their computed position so they center on it. */
const ICON_OFFSET_PX = 6;

/**
 * Lists the arrows and markers to draw, in render order. The earlier arrow
 * hides once the x-axis min is zoomed, and the later arrow once the max is,
 * because the issues they point to are then off the zoomed range.
 */
export function buildIssueAlerts({
  annotationActive,
  hasAfterIssues,
  hasBeforeIssues,
  marginLeft,
  marginRight,
  sortedPositionedIssues,
  xAxisMax,
  xAxisMin,
}: {
  annotationActive: boolean;
  hasAfterIssues: boolean;
  hasBeforeIssues: boolean;
  marginLeft: number;
  marginRight: number;
  sortedPositionedIssues: Array<[string, number]>;
  xAxisMax: number | null | undefined;
  xAxisMin: number | null | undefined;
}): IssueAlertModel[] {
  const issueCount = sortedPositionedIssues.length;
  const alerts: IssueAlertModel[] = [];

  if (hasBeforeIssues && xAxisMin == null) {
    alerts.push({
      key: 'issue-alert-before',
      id: 'before',
      label: '←',
      index: issueCount * -1,
      left: marginLeft - ICON_OFFSET_PX,
      highlightable: false,
    });
  }
  if (annotationActive) {
    sortedPositionedIssues.forEach(function addIssueMarker([id, left], index) {
      alerts.push({
        key: `issue-alert-${index}`,
        id,
        label: '!',
        index,
        left: left - ICON_OFFSET_PX,
        highlightable: true,
      });
    });
  }
  if (hasAfterIssues && xAxisMax == null) {
    alerts.push({
      key: 'issue-alert-final',
      id: 'final',
      label: '→',
      index: issueCount + 1,
      right: marginRight - ICON_OFFSET_PX,
      highlightable: false,
    });
  }
  return alerts;
}
